const normalizeId = (value) => String(value ?? '').trim();

const normalizeText = (value) => String(value || '').trim();

const toNumber = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

const toTimestamp = (value) => {
	if (!value) return 0;
	const time = new Date(value).getTime();
	return Number.isFinite(time) ? time : 0;
};

const latestTimestamp = (values = []) => {
	const latest = values
		.map((value) => ({ value, time: toTimestamp(value) }))
		.filter((entry) => entry.time > 0)
		.sort((a, b) => b.time - a.time)[0];
	return latest?.value || null;
};

export const DOCUMENT_STATUS_DEFINITIONS = [
	{ key: 'read_only', label: 'Read only', tone: 'muted' },
	{ key: 'awaiting_owner_signature', label: 'Awaiting owner signature', tone: 'warning' },
	{ key: 'awaiting_supplier_signature', label: 'Awaiting supplier signature', tone: 'warning' },
	{ key: 'completed', label: 'Completed', tone: 'success' },
	{ key: 'superseded', label: 'Superseded', tone: 'danger' },
	{ key: 'cancelled', label: 'Cancelled', tone: 'danger' },
	{ key: 'no_mobile_access', label: 'No mobile access', tone: 'muted' },
	{ key: 'not_assigned', label: 'Not assigned', tone: 'muted' },
	{ key: 'no_documents', label: 'No assigned documents', tone: 'muted' },
	{ key: 'unknown', label: 'Unknown', tone: 'muted' },
];

export const DOCUMENT_STATUS_FILTER_OPTIONS = DOCUMENT_STATUS_DEFINITIONS.filter(
	(status) => status.key !== 'unknown',
);

const STATUS_BY_KEY = new Map(DOCUMENT_STATUS_DEFINITIONS.map((status) => [status.key, status]));

export const getDocumentStatusDefinition = (key) =>
	STATUS_BY_KEY.get(key) || STATUS_BY_KEY.get('unknown');

export const hasSupplierMobileAccess = (entry = {}) => {
	const portal = entry?.portal || {};
	if (!portal?.hasProfile) return false;
	if (portal.enabled === false) return false;
	if (portal.mobileEnabled === false) return false;
	return Boolean(portal.phoneE164);
};

export const isActiveSupplierEntry = (entry = {}) => {
	const supplier = entry?.supplier || {};
	if (supplier.archived === true) return false;
	if (supplier.active === false || supplier.isActive === false) return false;
	return true;
};

export const getPortalStatusLabel = (portal = {}) => {
	const status = portal?.status || 'no_profile';
	if (status === 'active') return 'Active';
	if (status === 'pending_verification') return 'Needs verification';
	if (status === 'no_phone') return 'No phone';
	if (status === 'mobile_disabled') return 'Mobile disabled';
	if (status === 'disabled') return 'Disabled';
	return 'No profile';
};

export const getPortalStatusTone = (portal = {}) => {
	const status = portal?.status || 'no_profile';
	if (status === 'active') return 'success';
	if (status === 'pending_verification') return 'warning';
	return 'muted';
};

export const getDocumentTypeLabel = (document = {}) => {
	const mode = String(document?.completionMode || 'read_only');
	return mode === 'bilateral_signature' ? 'Bilateral signature' : 'Read only';
};

export const getActiveDocumentOptions = (documents = []) =>
	(Array.isArray(documents) ? documents : [])
		.filter((document) => document?.id)
		.filter((document) => document?.isArchived !== true)
		.filter((document) => Boolean(document?.file?.id))
		.slice()
		.sort((left, right) => {
			const leftOrder = toNumber(left?.sortOrder, 0);
			const rightOrder = toNumber(right?.sortOrder, 0);
			if (leftOrder !== rightOrder) return leftOrder - rightOrder;
			return normalizeText(left?.title).localeCompare(normalizeText(right?.title));
		});

const getLastActivityAt = (document = {}) => {
	const activity = document?.activity || {};
	const execution = document?.execution || {};
	return latestTimestamp([
		activity.viewedAt,
		activity.downloadedAt,
		execution.ownerSignedAt,
		execution.supplierSignedAt,
		execution.completedAt,
		execution.supersededAt,
		execution.cancelledAt,
	]);
};

const getCompletedAt = (document = {}) => {
	const execution = document?.execution || {};
	return (
		latestTimestamp([
			execution.completedAt,
			document?.completedAt,
		]) ||
		latestTimestamp([
			execution.supplierSignedAt,
			execution.ownerSignedAt,
		])
	);
};

export const getSupplierDocumentStatusKey = ({ supplierEntry, document, assigned = true } = {}) => {
	if (!assigned || !document?.id) return 'not_assigned';
	if (!hasSupplierMobileAccess(supplierEntry)) return 'no_mobile_access';

	const mode = String(document?.completionMode || 'read_only');

	if (mode === 'bilateral_signature') {
		const executionStatus = String(document?.executionStatus || document?.execution?.status || '');
		if (executionStatus === 'completed') return 'completed';
		if (executionStatus === 'awaiting_supplier_signature') return 'awaiting_supplier_signature';
		if (executionStatus === 'superseded') return 'superseded';
		if (executionStatus === 'cancelled') return 'cancelled';
		return 'awaiting_owner_signature';
	}

	return 'read_only';
};

const buildNoDocumentsRow = (supplierEntry = {}) => {
	const supplier = supplierEntry?.supplier || {};
	const status = getDocumentStatusDefinition('no_documents');
	return {
		id: `${normalizeId(supplier.id) || 'supplier'}:none`,
		supplierId: supplier.id ?? null,
		supplierName: supplier.name || 'Untitled supplier',
		supplierEmail: supplier.email || '',
		portalStatusLabel: getPortalStatusLabel(supplierEntry?.portal),
		portalStatusTone: getPortalStatusTone(supplierEntry?.portal),
		documentId: null,
		documentTitle: 'No assigned documents',
		documentFileName: '',
		typeLabel: '-',
		statusKey: status.key,
		statusLabel: status.label,
		statusTone: status.tone,
		lastActivityAt: null,
		assigned: false,
	};
};

const buildDocumentRow = ({
	supplierEntry = {},
	document = {},
	adminDocument = null,
	assigned = true,
} = {}) => {
	const supplier = supplierEntry?.supplier || {};
	const displayDocument = document?.id ? document : adminDocument || {};
	const statusKey = getSupplierDocumentStatusKey({
		supplierEntry,
		document: displayDocument,
		assigned,
	});
	const status = getDocumentStatusDefinition(statusKey);
	const documentId = displayDocument?.id ?? null;

	return {
		id: `${normalizeId(supplier.id) || 'supplier'}:${normalizeId(documentId) || 'document'}`,
		supplierId: supplier.id ?? null,
		supplierName: supplier.name || 'Untitled supplier',
		supplierEmail: supplier.email || '',
		portalStatusLabel: getPortalStatusLabel(supplierEntry?.portal),
		portalStatusTone: getPortalStatusTone(supplierEntry?.portal),
		documentId,
		documentTitle: displayDocument?.title || 'Untitled document',
		documentFileName: displayDocument?.file?.name || '',
		typeLabel: getDocumentTypeLabel(displayDocument),
		statusKey: status.key,
		statusLabel: status.label,
		statusTone: status.tone,
		completedAt: status.key === 'completed' ? getCompletedAt(displayDocument) : null,
		lastActivityAt: assigned ? getLastActivityAt(displayDocument) : null,
		assigned,
	};
};

export const buildDocumentStatusRows = ({
	suppliers = [],
	documents = [],
	selectedDocumentId = '',
} = {}) => {
	const supplierEntries = (Array.isArray(suppliers) ? suppliers : []).filter(isActiveSupplierEntry);
	const activeDocuments = getActiveDocumentOptions(documents);
	const documentsById = new Map(
		activeDocuments.map((document) => [normalizeId(document.id), document]),
	);
	const selectedId = normalizeId(selectedDocumentId);

	if (selectedId) {
		const selectedDocument = documentsById.get(selectedId) || null;
		if (!selectedDocument) return [];

		return supplierEntries.map((supplierEntry) => {
			const assignedDocument = (supplierEntry?.documents || []).find(
				(document) => normalizeId(document?.id) === selectedId,
			);
			return buildDocumentRow({
				supplierEntry,
				document: assignedDocument || selectedDocument,
				adminDocument: selectedDocument,
				assigned: Boolean(assignedDocument),
			});
		});
	}

	return supplierEntries.flatMap((supplierEntry) => {
		const supplierDocuments = Array.isArray(supplierEntry?.documents)
			? supplierEntry.documents.filter((document) => document?.id)
			: [];
		if (!supplierDocuments.length) return [buildNoDocumentsRow(supplierEntry)];

		return supplierDocuments.map((document) =>
			buildDocumentRow({
				supplierEntry,
				document,
				adminDocument: documentsById.get(normalizeId(document.id)) || null,
				assigned: true,
			}),
		);
	});
};
